import constate from 'constate';
import { FocusEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { useFormContext } from 'core/states';
import { isSubmitButtonBlur } from 'core/utils';
import { PricingSourceType } from 'pricing/enums';
import { CostSourceHelper, PricingHelper } from 'pricing/helpers';
import { useCostDecimals } from 'pricing/hooks';
import {
  getDefaultSpaAgreement,
  selectSpaRequestFieldNames,
  useSelectedSpaNotOfferedMessage,
} from 'quotes/components/QuotePartSelectSpa';
import { PriceBasedOn } from 'quotes/enums';
import { QuotePartHelper, QuotePartPricingHelper } from 'quotes/helpers';
import { ICustomerQuotePart } from 'quotes/types';
import { isRemoveSelectedSpaActionAvailable, isSelectSpaActionAvailable } from 'quotes/utils';

import { usePricingStateContext } from './pricingState';
import { useOneTimeSpaState } from './useOneTimeSpaState';

interface ICostFormFieldsProps {
  isShipAndDebitVendor: boolean;
  externalReplacementCost?: number;
  externalRepCostRegistrationNum?: string;
  quoteId?: number;
  quotePart?: ICustomerQuotePart;
}

const useCostFormFieldsState = ({
  isShipAndDebitVendor,
  externalReplacementCost,
  externalRepCostRegistrationNum,
  quoteId,
  quotePart,
}: ICostFormFieldsProps) => {
  const { control, errors, setValue, getValues, watch } = useFormContext();
  const costDecimals = useCostDecimals();

  const {
    changePriceSource,
    costData,
    initialCostData,
    priceDecimals,
    standardCostFieldName,
    marginFieldName,
    isAlternateVersionEnabled,
    disablePricingFields,
    isExternalDisabled,
    isCalculating,
    focusFieldWhileCalculating,
    kickPricingRequest,
    unifiedPartStatus,
    purchaseCostExceedsStandard,
    priceData,
    onCostingCustomChange,
    onPrioritizeLegacyCostChange,
    onBlurPurchaseCost,
    onChangePurchaseCost,
    onChangePricingDate,
    calculatePrices,
    calculateStandardCostForReplacementCost,
    resetReplacementStandardCostDedup,
    lastPricesRef,
    selectSpa,
    clearSelectedSpa,
    isReplaceSelectedSpaConfirmationOpen,
    replaceSelectedSpaAgreementCode,
    confirmReplaceSelectedSpa,
    cancelReplaceSelectedSpa,
    isSpaSelectionAvailable,
    isSelectedSpaNotOffered,
    setIsSelectedSpaNotOffered,
    buildSelectSpaRequest,
    markSelectSpaPress,
    pricingWindowMessage,
  } = usePricingStateContext();

  const isPriceLocked = !!priceData?.priceLocked;
  // Measured from the Quote Cost card's Validity Dates block; 0 while it is not shown.
  const [costValidityHeight, setCostValidityHeight] = useState(0);

  const isCostingCustomValue = watch('isCostingCustom');
  const isReplacementCostCustomValue = watch('isReplacementCostCustom');
  const prioritizeLegacyCostValue = watch('prioritizeLegacyCost');
  const purchaseCostValue = Number(watch('purchaseCost'));
  const replacementCostValue = Number(watch('replacementCost'));
  const shipmentTypeValue = watch('shipmentType');
  const standardCostValue = watch(standardCostFieldName);
  const replacementStandardCostValue = watch('replacementStandardCost');
  const priceBasedOn = watch('priceBasedOn') || PriceBasedOn.QUOTED;

  const displayReplacementCost = useMemo(
    () =>
      QuotePartHelper.displayReplacementCost({
        isAlternateVersionEnabled,
        isShipAndDebitVendor,
      }),
    [isAlternateVersionEnabled, isShipAndDebitVendor]
  );

  const allowOverrideReplacementCost = useMemo(
    () =>
      QuotePartHelper.allowOverrideReplacementCost({
        isAlternateVersionEnabled,
        isShipAndDebitVendor,
        shipmentTypeCode: shipmentTypeValue?.code,
      }),
    [isAlternateVersionEnabled, isShipAndDebitVendor, shipmentTypeValue?.code]
  );

  const requiredReplacementCost = useMemo(
    () =>
      QuotePartHelper.requiredReplacementCost({
        isAlternateVersionEnabled,
        isShipAndDebitVendor,
        purchaseCost: purchaseCostValue,
      }),
    [isAlternateVersionEnabled, isShipAndDebitVendor, purchaseCostValue]
  );

  const requiredVendorQuoteNumber = useMemo(
    () =>
      QuotePartHelper.requiredVendorQuoteNumber({
        isAlternateVersionEnabled,
        isCostingCustom: !!isCostingCustomValue,
        purchaseCost: purchaseCostValue,
      }),
    [isAlternateVersionEnabled, isCostingCustomValue, purchaseCostValue]
  );

  const requiredReplacementCostNumber = useMemo(
    () =>
      QuotePartHelper.requiredReplacementCostNumber({
        isAlternateVersionEnabled,
        isShipAndDebitVendor,
        purchaseCost: purchaseCostValue,
        replacementCost: replacementCostValue,
      }),
    [replacementCostValue, isAlternateVersionEnabled, isShipAndDebitVendor, purchaseCostValue]
  );

  const disabledIsReplacementCostCustom = useMemo(
    () =>
      !allowOverrideReplacementCost ||
      (requiredReplacementCost && isReplacementCostCustomValue && !externalReplacementCost),
    [
      allowOverrideReplacementCost,
      requiredReplacementCost,
      isReplacementCostCustomValue,
      externalReplacementCost,
    ]
  );

  useEffect(() => {
    if (!allowOverrideReplacementCost && isReplacementCostCustomValue) {
      setValue('isReplacementCostCustom', false);
      setValue('replacementCost', externalReplacementCost ?? null);
      setValue('replacementCostNumber', externalRepCostRegistrationNum ?? null);
    }
  }, [
    allowOverrideReplacementCost,
    externalRepCostRegistrationNum,
    externalReplacementCost,
    isReplacementCostCustomValue,
    setValue,
  ]);

  // Deferred-lifecycle invariant guard: in quoted mode the replacement standard cost must stay
  // empty (it materializes only when the replacement radio is selected). This is the single
  // enforcement point that holds regardless of which path wrote the value — a recalc response
  // handler, a re-seed keyed on the line's PERSISTED (replacement) mode, or any future writer — so
  // a quoted-mode recalc can never repopulate the card. Self-terminating: clearing to null makes
  // the condition false, so it does not loop. Never fires in replacement mode.
  useEffect(() => {
    if (priceBasedOn !== PriceBasedOn.REPLACEMENT && replacementStandardCostValue) {
      setValue('replacementStandardCost', null);
    }
  }, [priceBasedOn, replacementStandardCostValue, setValue]);

  // costData carries what the latest /prices response resolved; the part props are only a fallback
  // for a drawer that has not recalculated yet (they are captured when it opens and go stale).
  const liveExternalReplacementCost = costData?.externalReplacementCost ?? externalReplacementCost;
  const liveExternalRepCostRegistrationNum =
    costData?.externalRepCostRegistrationNum ?? externalRepCostRegistrationNum;

  const onReplacementCostCustomChange = useCallback(
    async ({ target: { checked } }) => {
      if (!checked) {
        setValue('replacementCost', liveExternalReplacementCost ?? null);
        setValue('replacementCostNumber', liveExternalRepCostRegistrationNum ?? null);

        // Only derive the standard cost when pricing is actually based on replacement;
        // otherwise it materializes lazily when the replacement radio is selected.
        if (liveExternalReplacementCost && getValues('priceBasedOn') === PriceBasedOn.REPLACEMENT) {
          await calculateStandardCostForReplacementCost(liveExternalReplacementCost);
        }
      }
    },
    [
      calculateStandardCostForReplacementCost,
      getValues,
      liveExternalRepCostRegistrationNum,
      liveExternalReplacementCost,
      setValue,
    ]
  );

  const onBlurReplacementCost = useCallback(
    async (event?: FocusEvent<HTMLInputElement>) => {
      const replacementCost = Number(getValues('replacementCost'));
      const currentPurchaseCost = Number(getValues('purchaseCost'));

      if (!replacementCost) {
        // Clearing the field must also clear the previously-computed standard cost,
        // otherwise REPLACEMENT-mode price/margin keep computing against a phantom value.
        setValue('replacementStandardCost', null);

        return;
      }

      // Blur caused by activating Save: the save path recomputes authoritatively, so skip
      // the wasted /quote/part/prices previews below (both the seed recalc and the derive).
      const blurToSubmit = isSubmitButtonBlur(event);

      let seedRequestStarted = false;

      if (!currentPurchaseCost) {
        setValue('purchaseCost', replacementCost);
        setValue('costSource.vendorQuoteNumber', getValues('replacementCostNumber'));

        // Mirrors a Switch click — flips form value + costData. costData is
        // refreshed synchronously via costDataRef in pricingState, so the
        // calculatePrices fired below reads the post-update CUSTOM source.
        await onCostingCustomChange({ target: { checked: true } });

        if (!blurToSubmit) {
          const { requestStarted, promise: seedPromise } = kickPricingRequest(onBlurPurchaseCost);

          seedRequestStarted = requestStarted;
          focusFieldWhileCalculating(event, requestStarted, 'replacementCostNumber');
          await seedPromise;
        }
      }

      // Derive the standard cost only when pricing is based on replacement; in quoted mode
      // the field stays an empty placeholder until the replacement radio is selected.
      if (!blurToSubmit && getValues('priceBasedOn') === PriceBasedOn.REPLACEMENT) {
        const { requestStarted, promise: derivePromise } = kickPricingRequest(() =>
          calculateStandardCostForReplacementCost(replacementCost)
        );

        // Hand off here except when the seed recalc above already launched one — its await makes
        // this point late (the user may have moved on). A dedup-dropped seed resolves instantly,
        // so the derive's hand-off is still effectively within the user's blur.
        if (!seedRequestStarted) {
          focusFieldWhileCalculating(event, requestStarted, 'replacementCostNumber');
        }

        await derivePromise;
      }
    },
    [
      calculateStandardCostForReplacementCost,
      focusFieldWhileCalculating,
      getValues,
      kickPricingRequest,
      onBlurPurchaseCost,
      onCostingCustomChange,
      setValue,
    ]
  );

  const onBlurReplacementCostNumber = useCallback(() => {
    if (!getValues('costSource.vendorQuoteNumber')) {
      setValue('costSource.vendorQuoteNumber', getValues('replacementCostNumber'));
    }
  }, [getValues, setValue]);

  const formatStandardCost = useCallback(
    (cost: number | null | undefined) => {
      if (!cost) {
        return DEFAULT_PLACEHOLDER;
      }

      return PricingHelper.formatPricing({
        amount: cost,
        decimals: costDecimals,
        fixedDecimals: true,
      });
    },
    [costDecimals]
  );

  // Mirrors the quoted card: External only while a live external replacement cost backs it, so a
  // line with neither a manual nor an external cost stops claiming a source it does not have.
  const replacementCostSourceLabel = useMemo(() => {
    if (isReplacementCostCustomValue) {
      return CostSourceHelper.getTypeLabel({ costSourceType: PricingSourceType.CUSTOM });
    }

    return CostSourceHelper.getTypeLabel({
      costSourceType: liveExternalReplacementCost ? PricingSourceType.EXTERNAL : undefined,
    });
  }, [isReplacementCostCustomValue, liveExternalReplacementCost]);

  const onPriceBasedOnChange = useCallback(
    async (event: { target: { value: string } }) => {
      const newValue = event.target.value;
      setValue('priceBasedOn', newValue);

      const switchingToReplacement = newValue === PriceBasedOn.REPLACEMENT;

      if (!switchingToReplacement) {
        // Leaving replacement mode: the derived standard cost was keyed on the OLD replacement
        // cost, so drop it (quoted mode shows the deferred placeholder + tooltip, per the deferred
        // lifecycle) and reset the derive dedup. Without the reset, editing the replacement cost in
        // quoted mode and switching straight back would short-circuit on inputsUnchanged and leave
        // the price computing off the stale standard cost.
        setValue('replacementStandardCost', null);
        resetReplacementStandardCostDedup();
      }

      // Turn off prioritizeLegacyCost when switching to replacement
      if (switchingToReplacement && getValues('prioritizeLegacyCost')) {
        setValue('prioritizeLegacyCost', false);
        await calculatePrices();
      }

      // Selecting replacement is the need-point for the deferred standard cost: derive it
      // now (basis = the manually overwritten replacement cost, else the external one) when
      // the line does not already carry one. The derive keeps the pricing margin and
      // reprices, so mirror the already-populated switch path and bail out once it lands.
      if (switchingToReplacement && !Number(getValues('replacementStandardCost'))) {
        const pricingMarginVal = getValues('pricingMargin');
        const hasPricingMargin = pricingMarginVal != null && !isNaN(parseFloat(pricingMarginVal));

        const overwriteReplacementCost = Number(getValues('replacementCost'));
        const basis =
          getValues('isReplacementCostCustom') && overwriteReplacementCost > 0
            ? overwriteReplacementCost
            : Number(externalReplacementCost);

        if (basis > 0) {
          await calculateStandardCostForReplacementCost(basis);
        }

        if (Number(getValues('replacementStandardCost')) > 0) {
          // A kept margin means the repriced value is a custom, margin-driven price — mark
          // the source custom to match the branch taken when the std cost was already
          // present (so isPricingCustom rides the save). A fresh line with no margin leaves
          // the source untouched, mirroring the no-margin branch below.
          if (hasPricingMargin) {
            changePriceSource(PricingSourceType.CUSTOM);
          }

          return;
        }
      }

      const stdCost = Number(getValues(standardCostFieldName));
      const activeCost = switchingToReplacement
        ? Number(getValues('replacementStandardCost'))
        : stdCost;

      // No active cost available → clear pricing fields and reset source
      if (!activeCost) {
        setValue('pricingMargin', null);
        setValue('price', null);
        changePriceSource(null);

        return;
      }

      // Has margin → recalculate price from new active cost
      const pricingMarginVal = getValues('pricingMargin');
      const currentPricingMargin = parseFloat(pricingMarginVal);

      if (pricingMarginVal != null && !isNaN(currentPricingMargin)) {
        // When switching back to quoted, compare margins to detect if the user
        // changed the pricing margin. If unchanged, use the precise discounted
        // price and restore the original API source to avoid rounding drift
        // (see comment in processPricingResponse).
        const { discountedPrice, priceSourceType } = lastPricesRef.current;
        const switchingToQuoted = !switchingToReplacement;

        if (switchingToQuoted && discountedPrice && stdCost) {
          const discountedPriceMargin = PricingHelper.computeGpmPercentage(
            stdCost,
            discountedPrice
          );

          if (currentPricingMargin === discountedPriceMargin) {
            setValue('price', discountedPrice);
            setValue(marginFieldName, discountedPriceMargin);

            changePriceSource(priceSourceType);

            return;
          }
        }

        // Margin was changed by the user → recalculate from rounded margin
        changePriceSource(PricingSourceType.CUSTOM);

        const newPrice = PricingHelper.computeAndFormatPrice(
          activeCost,
          currentPricingMargin / 100,
          { decimals: priceDecimals }
        );

        setValue('price', newPrice);

        if (stdCost) {
          setValue(marginFieldName, PricingHelper.computeGpmPercentage(stdCost, newPrice));
        }

        return;
      }

      // No margin but has price → derive margin from price
      const priceValue = Number(getValues('price'));

      if (priceValue) {
        setValue('pricingMargin', PricingHelper.computeGpmPercentage(activeCost, priceValue));
      }
    },
    [
      setValue,
      getValues,
      standardCostFieldName,
      calculatePrices,
      calculateStandardCostForReplacementCost,
      resetReplacementStandardCostDedup,
      externalReplacementCost,
      changePriceSource,
      lastPricesRef,
      priceDecimals,
      marginFieldName,
    ]
  );

  const isCostSourceExternal = costData?.costSourceType === PricingSourceType.EXTERNAL;
  const isCostSourceSelectedSpa = costData?.costSourceType === PricingSourceType.SELECTED_SPA;

  // Overwrite on → always "Manually Entered", even before a cost is typed. Otherwise a
  // loaded part that resolved to no source shows "No Cost" (vs "-" when no part selected).
  const isManualCostSource = !!isCostingCustomValue;
  const isNoCostSource = !isManualCostSource && !!watch('part') && !costData?.costSourceType;
  // prioritizeLegacyCost excluded deliberately: processCostResponse skips the replacement writes
  // entirely on those lines, so the input can hold a value the response never vouched for. Falling
  // through to the label's "-" says "not determined here"; "No Cost" would be a positive claim
  // contradicting a populated input. Same distinction isNoCostSource draws with its `part` guard.
  const isNoReplacementCostSource =
    !isReplacementCostCustomValue &&
    !prioritizeLegacyCostValue &&
    !!watch('part') &&
    !liveExternalReplacementCost;
  const costSourceDisplayData = isManualCostSource
    ? QuotePartPricingHelper.sanitizeCostDataForSourceType(costData, PricingSourceType.CUSTOM)
    : costData;
  // The CUSTOM sanitize drops the pick, so a pending Overwrite hides it before the revoke lands.
  const selectedSpa = costSourceDisplayData?.selectedSpa ?? null;
  // Display data too: a pending Overwrite already takes the line off its default.
  const defaultSpa = getDefaultSpaAgreement(costSourceDisplayData);

  const oneTimeSpa = useOneTimeSpaState({
    costData,
    costSourceDisplayData,
    initialCostData,
    externalReplacementCost: liveExternalReplacementCost,
    isAlternateVersionEnabled,
    isCostingCustom: !!isCostingCustomValue,
    isShipAndDebitVendor,
    purchaseCost: purchaseCostValue,
    replacementCost: replacementCostValue,
    quotePart,
  });

  const hasPart = !!watch('part') || !!quotePart?.partArr?.number;
  const hasSelectedSpa = !!selectedSpa?.spaCostSource;
  // The form's last /prices answer, not the row: an edited line may price differently.
  const canSelectSpa = isSelectSpaActionAvailable({
    hasPart,
    hasSelectedSpa,
    isAlternateVersionEnabled,
    isSpaSelectionAvailable,
    quotePart,
  });
  const canRemoveSelectedSpa = isRemoveSelectedSpaActionAvailable({
    hasPart,
    hasSelectedSpa,
    isAlternateVersionEnabled,
    quotePart,
  });

  // Subscribes to the fields the request reads; the builder reads the values themselves.
  watch(selectSpaRequestFieldNames);

  const selectedSpaNotOfferedMessage = useSelectedSpaNotOfferedMessage(
    selectedSpa,
    selectedSpa?.spaCostSource ? buildSelectSpaRequest() : null,
    isSelectedSpaNotOffered
  );

  // The drawer's list answer for the pick, which holds until the next pick, UOM re-ask or reset.
  const onHeldAgreementOffered = useCallback(
    (isOffered: boolean) => setIsSelectedSpaNotOffered(!isOffered),
    [setIsSelectedSpaNotOffered]
  );

  return {
    quoteId,
    quotePart,
    canSelectSpa,
    canRemoveSelectedSpa,
    buildSelectSpaRequest,
    markSelectSpaPress,
    pricingWindowMessage,
    selectedSpaNotOfferedMessage,
    onHeldAgreementOffered,
    getValues,
    control,
    errors,
    costData,
    costDecimals,
    isAlternateVersionEnabled,
    disablePricingFields,
    isExternalDisabled,
    isCalculating,
    isPriceLocked,
    unifiedPartStatus,
    liveExternalReplacementCost,
    isShipAndDebitVendor,
    displayReplacementCost,
    priceBasedOn,
    isCostingCustomValue,
    isReplacementCostCustomValue,
    prioritizeLegacyCostValue,
    purchaseCostExceedsStandard,
    purchaseCostValue,
    replacementCostValue,
    standardCostValue,
    replacementStandardCostValue,
    allowOverrideReplacementCost,
    requiredReplacementCost,
    requiredReplacementCostNumber,
    requiredVendorQuoteNumber,
    disabledIsReplacementCostCustom,
    isCostSourceExternal,
    isCostSourceSelectedSpa,
    isNoCostSource,
    isNoReplacementCostSource,
    costSourceDisplayData,
    selectedSpa,
    defaultSpa,
    oneTimeSpa,
    costValidityHeight,
    setCostValidityHeight,
    selectSpa,
    clearSelectedSpa,
    isReplaceSelectedSpaConfirmationOpen,
    replaceSelectedSpaAgreementCode,
    confirmReplaceSelectedSpa,
    cancelReplaceSelectedSpa,
    formatStandardCost,
    replacementCostSourceLabel,
    onReplacementCostCustomChange,
    onBlurReplacementCost,
    onBlurReplacementCostNumber,
    onCostingCustomChange,
    onPrioritizeLegacyCostChange,
    onBlurPurchaseCost,
    onChangePurchaseCost,
    onChangePricingDate,
    onPriceBasedOnChange,
  };
};

export const [CostFormFieldsProvider, useCostFormFieldsContext] = constate(useCostFormFieldsState);
